// The standard list page, shared by Files, Forms, Redactions, and Investigations
// so they read as one system: page header (title, subtitle, primary action),
// toolbar (search, optional filters, optional tile/list toggle), then either a
// table or a grid of cards.
//
// It is presentational. The page keeps its own data loading and decides what
// to filter and how to sort (in memory or on the server) and hands over the
// items to show. What it adds on top of the markup:
//   - sortable column headers (controlled `sort` / `onSort`)
//   - resizable columns that remember their widths (`resizeStorageKey`)
//   - row selection with checkboxes and a collapsing action bar (`selection`)
//   - a tile view built from `renderCard`, optionally grouped (`cardGroups`)
//   - loading, empty, and no-match states
//
//   <ListPage
//     title="Investigations" subtitle="..." action={<Button>New</Button>}
//     items={filtered} getKey={(i) => i.id}
//     columns={[{ key: "name", header: "Name", sortKey: "name", render: (i) => i.name }]}
//     sort={sort} onSort={setSort} onRowClick={(i) => navigate(...)}
//     search={{ value, onChange, placeholder }}
//     viewMode={viewMode} onViewModeChange={setViewMode} renderCard={(i) => <ItemCard .../>}
//   />
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { ReactNode, useRef } from "react";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { usePageFileDrop } from "../../hooks/usePageFileDrop";
import ColumnResizer from "./ColumnResizer";
import { CardGrid } from "./ItemCard";
import { ListViewToolbar, SortableTh, SortState, ViewMode } from "./ListViewControls";
import Reveal from "./Reveal";

// The app's checkbox (blue-500, not DaisyUI's theme primary). Corners are
// squared globally in index.css.
export const listCheckboxCls =
  "checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500";

const SELECT_COL = 36; // 16px left padding + the 20px checkbox, no right padding
const FILLER_MIN = 90;

type Breakpoint = "md" | "lg";
const CELL_HIDE: Record<Breakpoint, string> = { md: "hidden md:table-cell", lg: "hidden lg:table-cell" };
const COL_HIDE: Record<Breakpoint, string> = { md: "hidden md:table-column", lg: "hidden lg:table-column" };

export interface ListColumn<T, K extends string = string> {
  key: string;
  header: ReactNode;
  render: (item: T, index: number) => ReactNode;
  // Makes the header clickable (needs `sort` / `onSort` on the page).
  sortKey?: K;
  defaultDir?: "asc" | "desc";
  // Resizable mode only: a width makes the column draggable; a column with no
  // width is the one that takes up leftover space (usually the last).
  width?: number;
  minWidth?: number;
  // Hidden below this breakpoint.
  hideBelow?: Breakpoint;
  // Cell content is cut off with an ellipsis instead of wrapping.
  truncate?: boolean;
  className?: string;
  // Extra classes for the header cell only.
  headerClassName?: string;
  align?: "right";
}

export interface ListSelection<T> {
  // Keys (from getKey) of the selected items, across pages if the page keeps them.
  selected: ReadonlySet<string>;
  onToggle: (item: T) => void;
  // A string disables the checkbox and is shown as its tooltip.
  disabledReason?: (item: T) => string | undefined;
  // Singular noun for "N files selected".
  noun: string;
  // Extra text beside the count (e.g. a limit notice).
  note?: ReactNode;
  // The action buttons in the bar.
  actions: ReactNode;
}

export interface ListEmptyState {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}

export interface CardGroup<T> {
  key: string;
  title: string;
  icon?: ReactNode;
  items: T[];
}

export interface ListPageProps<T, K extends string = string> {
  title: string;
  subtitle?: ReactNode;
  // The page's primary button.
  action?: ReactNode;

  items: T[];
  getKey: (item: T) => string;
  columns: ListColumn<T, K>[];
  sort?: SortState<K>;
  onSort?: (sort: SortState<K>) => void;
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T) => string | undefined;
  // Dimmed and not clickable (e.g. still processing).
  rowDisabled?: (item: T) => boolean;

  // Toolbar. Pass `search` to show it; `filters` render beside it.
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  filters?: ReactNode;
  // Tile view: provide renderCard (and optionally cardGroups) plus the view
  // mode state. Without renderCard the page is table-only and has no toggle.
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  renderCard?: (item: T, index: number) => ReactNode;
  cardGroups?: (items: T[]) => CardGroup<T>[];

  selection?: ListSelection<T>;
  // Persist and enable draggable column widths under this localStorage key.
  resizeStorageKey?: string;

  loading?: boolean;
  // Shown instead of the toolbar and list when there is nothing at all.
  empty?: ListEmptyState;
  // True when search or filters are narrowing the list; decides between the
  // `empty` block and the "no match" message when items is empty.
  isFiltered?: boolean;
  noMatch?: ReactNode;

  // Accept files dragged onto the page. The page decides what to do with them
  // (the Files page opens its upload modal with them loaded).
  onFilesDropped?: (files: File[]) => void;
  dropMessage?: string;

  // Below the list (pagination).
  footer?: ReactNode;
  // Modals and other siblings.
  children?: ReactNode;
}

export default function ListPage<T, K extends string = string>({
  title,
  subtitle,
  action,
  items,
  getKey,
  columns,
  sort,
  onSort,
  onRowClick,
  rowClassName,
  rowDisabled,
  search,
  filters,
  viewMode,
  onViewModeChange,
  renderCard,
  cardGroups,
  selection,
  resizeStorageKey,
  loading,
  empty,
  isFiltered,
  noMatch,
  onFilesDropped,
  dropMessage = "Drop files to add them",
  footer,
  children,
}: ListPageProps<T, K>) {
  // Page-level file drop (shared with other pages that accept dropped files).
  const { dragging, dropHandlers } = usePageFileDrop(onFilesDropped);

  const resizable = Boolean(resizeStorageKey);
  const [storedWidths, setWidths] = useLocalStorageState<Record<string, number | undefined>>(
    resizeStorageKey || "pnai-list-widths-unused",
    {},
  );
  const widthOf = (c: ListColumn<T, K>) => Math.max(c.minWidth ?? 60, storedWidths[c.key] ?? c.width ?? 0);

  // Keep the last non-zero count so the bar's text does not flash "0 selected"
  // while it collapses.
  const lastCount = useRef(0);
  const selectedCount = selection?.selected.size ?? 0;
  if (selectedCount > 0) lastCount.current = selectedCount;
  const shownCount = selectedCount > 0 ? selectedCount : lastCount.current;

  const hasToggle = Boolean(renderCard && viewMode && onViewModeChange);
  const showGrid = hasToggle && viewMode === "grid";
  const showEmptyBlock = !loading && items.length === 0 && !isFiltered && Boolean(empty);
  const showToolbar = Boolean(search || filters || hasToggle) && !showEmptyBlock;

  const tableMinWidth = resizable
    ? (selection ? SELECT_COL : 0) +
      columns.reduce((n, c) => n + (c.width !== undefined ? widthOf(c) : c.minWidth ?? FILLER_MIN), 0)
    : undefined;

  const renderHeader = (c: ListColumn<T, K>) => {
    const resizer =
      resizable && c.width !== undefined ? (
        <ColumnResizer
          width={widthOf(c)}
          min={c.minWidth ?? 60}
          onResize={(w) => setWidths((prev) => ({ ...prev, [c.key]: w }))}
          onReset={() => setWidths((prev) => ({ ...prev, [c.key]: undefined }))}
        />
      ) : undefined;
    const cls = cn(c.hideBelow && CELL_HIDE[c.hideBelow], c.align === "right" && "text-right", c.headerClassName);
    if (c.sortKey && sort && onSort) {
      return (
        <SortableTh<K>
          key={c.key}
          label={typeof c.header === "string" ? c.header : c.key}
          sortKey={c.sortKey}
          sort={sort}
          onSort={onSort}
          defaultDir={c.defaultDir}
          className={cls}
          resizer={resizer}
        />
      );
    }
    return (
      <th key={c.key} className={cn("relative", cls)}>
        {c.header}
        {resizer}
      </th>
    );
  };

  const table = (
    <div className="bg-base-100 border border-base-300 rounded-lg overflow-x-auto">
      <table
        className={cn("table table-sm", resizable ? "table-fixed" : "w-full")}
        style={resizable ? { width: "100%", minWidth: tableMinWidth } : undefined}
      >
        {resizable && (
          <colgroup>
            {selection && <col style={{ width: SELECT_COL }} />}
            {columns.map((c) => (
              <col
                key={c.key}
                className={c.hideBelow ? COL_HIDE[c.hideBelow] : undefined}
                style={c.width !== undefined ? { width: widthOf(c) } : undefined}
              />
            ))}
          </colgroup>
        )}
        <thead>
          <tr className="text-xs text-base-content/60 border-b border-base-300">
            {selection && <th className="pr-0" />}
            {columns.map(renderHeader)}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const key = getKey(item);
            const disabled = rowDisabled?.(item) ?? false;
            const isSelected = selection?.selected.has(key) ?? false;
            const reason = selection?.disabledReason?.(item);
            return (
              <Reveal
                as="tr"
                key={key}
                index={i}
                className={cn(
                  "group border-b border-base-200 dark:border-base-300 last:border-b-0",
                  disabled ? "opacity-80" : onRowClick && "hover:bg-base-200 cursor-pointer",
                  isSelected && "bg-base-200/60",
                  rowClassName?.(item),
                )}
                onClick={onRowClick && !disabled ? () => onRowClick(item) : undefined}
              >
                {selection && (
                  <td className="pr-0" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label="Select"
                      // block: an inline checkbox sits on the text baseline, a few px above the row's centre
                      className={cn(listCheckboxCls, "block")}
                      checked={isSelected}
                      disabled={Boolean(reason) && !isSelected}
                      title={reason}
                      onChange={() => selection.onToggle(item)}
                    />
                  </td>
                )}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      c.hideBelow && CELL_HIDE[c.hideBelow],
                      c.truncate && "truncate",
                      c.align === "right" && "text-right whitespace-nowrap",
                      c.className,
                    )}
                    onClick={c.align === "right" ? (e) => e.stopPropagation() : undefined}
                  >
                    {c.render(item, i)}
                  </td>
                ))}
              </Reveal>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderCards = (list: T[]) => (
    <CardGrid>
      {list.map((item, i) => (
        <Reveal key={getKey(item)} index={i}>
          {renderCard!(item, i)}
        </Reveal>
      ))}
    </CardGrid>
  );

  // Built only when the tile view is showing: table-only lists have no renderCard.
  const renderGrid = () =>
    cardGroups ? (
    <>
      {cardGroups(items)
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.key}>
            <div className="flex items-center gap-2 mb-3">
              {g.icon}
              <h2 className="text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider">{g.title}</h2>
            </div>
            {renderCards(g.items)}
          </div>
        ))}
    </>
    ) : (
      renderCards(items)
    );

  return (
    <div className="flex-1 overflow-auto p-6 space-y-6 relative" {...dropHandlers}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold">{title}</h1>
          {subtitle && <p className="text-base-content/60 text-sm mt-1">{subtitle}</p>}
        </div>
        {action}
      </div>

      {showToolbar &&
        (search ? (
          <ListViewToolbar
            search={search.value}
            onSearchChange={search.onChange}
            searchPlaceholder={search.placeholder}
            viewMode={hasToggle ? viewMode : undefined}
            onViewModeChange={hasToggle ? onViewModeChange : undefined}
          >
            {filters}
          </ListViewToolbar>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">{filters}</div>
        ))}

      {/* Collapses to nothing (and drops its own top margin) when nothing is
          selected; slides open and fades in when the first item is ticked. */}
      {selection && (
        <div
          aria-hidden={selectedCount === 0}
          {...(selectedCount === 0 ? ({ inert: "" } as Record<string, string>) : {})}
          className={cn(
            "grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out motion-reduce:transition-none",
            selectedCount > 0 ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 !mt-0 pointer-events-none",
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-base-300 bg-base-200/50 px-4 py-2">
              <span className="text-sm font-medium text-base-content mr-auto">
                {shownCount} {selection.noun}
                {shownCount === 1 ? "" : "s"} selected
                {selection.note && <span className="ml-2 text-xs font-normal text-base-content/60">{selection.note}</span>}
              </span>
              {selection.actions}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 rounded-lg bg-base-300" />
          ))}
        </div>
      ) : showEmptyBlock && empty ? (
        <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center">
          <div className="mx-auto mb-3 flex justify-center text-base-content/30">{empty.icon}</div>
          <p className="font-medium text-base-content">{empty.title}</p>
          {empty.description && <p className="text-sm text-base-content/60 mb-4">{empty.description}</p>}
          {empty.action}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <MagnifyingGlassIcon className="h-10 w-10 mx-auto text-gray-300 dark:text-base-content/30" />
          <p className="mt-3 text-sm text-base-content/60">{noMatch ?? "Nothing matches your search."}</p>
        </div>
      ) : showGrid ? (
        renderGrid()
      ) : (
        table
      )}

      {footer}
      {children}
      {/* Last child and margin-free: the container's space-y spacing and the
          page layout must not react to this overlay appearing. */}
      {dragging && (
        <div className="pointer-events-none absolute inset-2 !m-0 z-20 rounded-xl border-2 border-dashed border-blue-500 bg-blue-500/5 flex items-start justify-center pt-24">
          <span className="rounded-full bg-blue-500 text-white text-sm font-medium px-4 py-2 shadow-lg">{dropMessage}</span>
        </div>
      )}
    </div>
  );
}
